import { useCallback, useMemo } from 'react';
import type { CSSProperties } from 'react';
import { theme, type MenuProps } from 'antd';
import { AppButton, AppDropdown } from '@/components/atoms';
import type { PatientFormData } from '@pages/main/patient/types';
import {
  coercePersistedHistoryRowId,
  getInsuranceHistoryDropdownParts,
  HEALTH_INSURANCE_HISTORY_QUAL_DATE_COLUMN_CH,
  healthInsuranceHistoryDropdownEligibleLegacy,
  sortInsuranceCardsForHistoryDropdown,
} from '@pages/main/patient/utils/patientHistoryHelpers';
import { historyDropdownPopupStyle } from './PatientSectionShared.helpers';

interface HealthInsuranceHistoryMenuProps {
  isEditMode: boolean;
  isCurrent: boolean;
  disabled: boolean;
  patientData?: PatientFormData | null;
  watchedHealthInsuranceCardId: unknown;
  healthInsuranceHistoryDraftByUrl: boolean;
  onHistorySwitch?: (type: 'health_insurance_card', historyId: number) => void;
  onHistoryDelete?: (type: 'health_insurance_card', historyId: number) => void;
  onHealthInsuranceHistoryCreateCancel?: () => void | Promise<void>;
}

export function useHealthInsuranceHistoryMenu({
  isEditMode,
  isCurrent,
  disabled,
  patientData,
  watchedHealthInsuranceCardId,
  healthInsuranceHistoryDraftByUrl,
  onHistorySwitch,
  onHistoryDelete,
  onHealthInsuranceHistoryCreateCancel,
}: HealthInsuranceHistoryMenuProps) {
  const { token } = theme.useToken();

  const historyCreateCancelButtonStyle = disabled
    ? undefined
    : {
        '--patient-history-cancel-bg': token.colorWarning,
        '--patient-history-cancel-bg-hover': token.colorWarningHover,
        '--patient-history-cancel-border': token.colorWarning,
        '--patient-history-cancel-border-hover': token.colorWarningHover,
        '--patient-history-cancel-text': token.colorTextLightSolid,
      } as CSSProperties;

  const sortedInsuranceCards = useMemo(
    () =>
      patientData?.health_insurance_cards?.length
        ? sortInsuranceCardsForHistoryDropdown(patientData.health_insurance_cards)
        : [],
    [patientData?.health_insurance_cards]
  );

  const handleInsuranceHistorySwitch = useCallback(
    (historyId: number) => {
      onHistorySwitch?.('health_insurance_card', historyId);
    },
    [onHistorySwitch]
  );

  const historyRecordMenuItems = useMemo(
    () =>
      sortedInsuranceCards.map(card => {
        const parts = getInsuranceHistoryDropdownParts(card);
        const qualColStyle = {
          width: `${HEALTH_INSURANCE_HISTORY_QUAL_DATE_COLUMN_CH}ch`,
        } as const;
        const hasAnyDate = Boolean(parts.qualYmd || parts.expYmd);
        return {
          key: card.id?.toString() || `card-${card.qualification_date}`,
          label: (
            <span className="inline-flex max-w-none items-start gap-2 whitespace-nowrap">
              <i
                className={
                  card.is_current
                    ? 'fa fa-dot-circle-o mt-0.5 shrink-0 text-blue-600'
                    : 'fa fa-circle-o mt-0.5 shrink-0 text-gray-500'
                }
                aria-hidden="true"
              />
              <span className="inline-flex min-w-0 flex-nowrap items-baseline gap-x-0 text-sm">
                <span
                  className="inline-block shrink-0 whitespace-nowrap pr-1 text-left font-mono tabular-nums tracking-tight"
                  style={qualColStyle}
                >
                  {parts.qualYmd}
                </span>
                <span className="shrink-0 whitespace-nowrap">から</span>
                {hasAnyDate ? (
                  <span
                    className="inline-block shrink-0 whitespace-nowrap text-left font-mono tabular-nums tracking-tight"
                    style={qualColStyle}
                  >
                    {parts.expYmd}
                  </span>
                ) : (
                  <span className="shrink-0 text-gray-500">（日付なし）</span>
                )}
              </span>
            </span>
          ),
          onClick: () => handleInsuranceHistorySwitch(card.id!),
        };
      }),
    [sortedInsuranceCards, handleInsuranceHistorySwitch]
  );

  const currentInsuranceCardForHeader =
    patientData?.health_insurance_cards?.find(c => c.is_current) ||
    patientData?.health_insurance_cards?.[0];
  const idFromPatientData =
    coercePersistedHistoryRowId(currentInsuranceCardForHeader?.id) ??
    coercePersistedHistoryRowId(
      (currentInsuranceCardForHeader as { health_insurance_card_id?: unknown } | undefined)
        ?.health_insurance_card_id
    );
  const watchedCoerced = coercePersistedHistoryRowId(watchedHealthInsuranceCardId);
  const displayedInsuranceCardId = healthInsuranceHistoryDraftByUrl
    ? watchedCoerced
    : (watchedCoerced ?? idFromPatientData);
  const hasPersistedHealthInsuranceCardId = displayedInsuranceCardId != null;

  const showHealthInsuranceHistoryCreateCancel =
    isEditMode &&
    !!patientData?.id &&
    (patientData?.health_insurance_cards?.length ?? 0) > 0 &&
    (!hasPersistedHealthInsuranceCardId || healthInsuranceHistoryDraftByUrl);

  const legacyInsuranceHistoryDropdownEligible = useMemo(
    () => healthInsuranceHistoryDropdownEligibleLegacy(patientData?.health_insurance_cards),
    [patientData?.health_insurance_cards]
  );

  const showHealthInsuranceHistoryHeader =
    isEditMode &&
    !!patientData?.id &&
    (showHealthInsuranceHistoryCreateCancel || legacyInsuranceHistoryDropdownEligible);

  const historyDropdownItems = useMemo((): MenuProps['items'] => {
    const newHistoryLabel =
      isEditMode && !isCurrent ? '新しい履歴を入力する' : '新しい情報を入力する';
    const items: MenuProps['items'] = [
      {
        key: 'new-history',
        label: newHistoryLabel,
        className: 'text-blue-600',
        onClick: () => handleInsuranceHistorySwitch(0),
      },
    ];
    // A2 edit.blade: divider + list only when count() > 1; single row -> 新しい情報のみ
    if (historyRecordMenuItems.length > 1) {
      items.push({ type: 'divider', key: 'divider-insurance-history' });
      items.push(...historyRecordMenuItems);
    }
    return items;
  }, [historyRecordMenuItems, isCurrent, isEditMode, handleInsuranceHistorySwitch]);

  const historyMenu = showHealthInsuranceHistoryHeader ? (
    <div className="flex gap-2">
      {showHealthInsuranceHistoryCreateCancel && onHealthInsuranceHistoryCreateCancel ? (
        <AppButton
          type="default"
          size="small"
          className="!border-[var(--patient-history-cancel-border)] !bg-[var(--patient-history-cancel-bg)] !text-[var(--patient-history-cancel-text)] hover:!border-[var(--patient-history-cancel-border-hover)] hover:!bg-[var(--patient-history-cancel-bg-hover)]"
          style={historyCreateCancelButtonStyle}
          disabled={disabled}
          onClick={() => void onHealthInsuranceHistoryCreateCancel()}
        >
          履歴作成取消
        </AppButton>
      ) : (
        <>
          {/*
           * A2: 履歴削除 float-left, 履歴 float-right — DOM order: delete then dropdown.
           * Show when count>1 && persisted card id (not only 履歴表示).
           */}
          {onHistoryDelete &&
            !!patientData?.id &&
            (patientData?.health_insurance_cards?.length ?? 0) > 1 &&
            !healthInsuranceHistoryDraftByUrl &&
            hasPersistedHealthInsuranceCardId && (
              <AppButton
                type="default"
                size="small"
                danger
                disabled={disabled}
                onClick={() =>
                  onHistoryDelete('health_insurance_card', displayedInsuranceCardId as number)
                }
                className={disabled ? '!bg-white/20 !text-white/40 !border-white/10' : ''}
              >
                履歴削除
              </AppButton>
            )}
          <AppDropdown
            menu={{
              items: historyDropdownItems,
              style: historyDropdownPopupStyle,
            }}
            styles={{
              root: historyDropdownPopupStyle,
            }}
            trigger={['click']}
            placement="bottomRight"
            disabled={disabled}
          >
            <AppButton
              type="default"
              size="small"
              disabled={disabled}
              className={disabled ? '!bg-white/20 !text-white/60 !border-white/20' : ''}
            >
              履歴 <i className="fa fa-angle-down" aria-hidden="true" />
            </AppButton>
          </AppDropdown>
        </>
      )}
    </div>
  ) : null;

  return {
    historyMenu,
    showHealthInsuranceHistoryCreateCancel,
  };
}
